import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { PaymentService } from '../payment.service';
import { CryptoPayoutStatus, PaymentRequest } from '../payment.type';
import { Viewport } from '../../shared/viewport';
import { PaymentState } from '../payment.state';
import { statusClass } from '../payment-format';

/**
 * The Payments "Payment requests" tab: suppliers asking to be paid. Opening one
 * shows what it covers, ready to be paid in one step.
 */
@Component({
  selector: 'app-payment-requests',
  imports: [
    CommonModule, FormsModule, MatTableModule, MatButtonModule, MatIconModule, MatFormFieldModule,
    MatSelectModule, MatInputModule, MatProgressSpinnerModule, MatPaginatorModule,
  ],
  templateUrl: './payment-requests.html',
  styleUrl: './payment-requests.scss',
})
export class PaymentRequests implements OnInit {
  readonly state = inject(PaymentState);
  private paymentService = inject(PaymentService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  readonly isMobile$ = inject(Viewport).isMobile$;

  readonly statusClass = statusClass;
  private readonly cryptoLabels: Record<CryptoPayoutStatus, string> = {
    Queued: 'queued', Signed: 'sending', Confirmed: 'sent', Failed: 'not sent',
  };

  cryptoLabel(status: CryptoPayoutStatus): string {
    return this.cryptoLabels[status] ?? status;
  }
  readonly pageSizeOptions = [25, 50, 100];
  readonly columns = ['created', 'supplier', 'amount', 'via', 'status', 'notes', 'open'];

  requests: PaymentRequest[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 25;
  loading = false;
  error: string | null = null;

  /** Pending by default — that is what still needs an answer. '' = all. */
  status = 'Pending';

  /** The payout method name the request was made with; '' = any. */
  payVia = '';
  payViaOptions: string[] = [];
  /** datetime-local values (the browser's time zone), '' = open-ended. */
  createdFrom = '';
  createdTo = '';
  /** Requested amount in Toman, null = open-ended. */
  minAmount: number | null = null;
  maxAmount: number | null = null;

  get hasExtraFilters(): boolean {
    return !!(this.payVia || this.createdFrom || this.createdTo || this.minAmount != null || this.maxAmount != null);
  }

  ngOnInit(): void {
    this.paymentService.getPaymentRequestPayVia().subscribe({
      next: names => this.payViaOptions = names,
      error: () => this.payViaOptions = [],
    });
    this.load();
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.pageIndex = 0; this.load(); });
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getPaymentRequests({
      supplierId: this.state.supplierId || undefined,
      status: this.status || undefined,
      payVia: this.payVia || undefined,
      createdFrom: toIso(this.createdFrom),
      // The picker stops at the minute; the backend's bound is exclusive, so
      // move it to the next minute to keep the whole chosen minute in.
      createdTo: toIso(this.createdTo, 60_000),
      minAmount: this.minAmount ?? undefined,
      maxAmount: this.maxAmount ?? undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    }).subscribe({
      next: res => {
        this.requests = res.requests;
        this.total = res.total;
        this.loading = false;
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load payment requests'; this.loading = false; },
    });
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.load();
  }

  clearExtraFilters(): void {
    this.payVia = '';
    this.createdFrom = '';
    this.createdTo = '';
    this.minAmount = null;
    this.maxAmount = null;
    this.onFilterChange();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  open(r: PaymentRequest): void {
    this.router.navigate(['/payments/requests', r.id]);
  }
}

/** A datetime-local value (local time) as an ISO timestamp, shifted by offsetMs. */
function toIso(local: string, offsetMs = 0): string | undefined {
  if (!local) return undefined;
  const t = new Date(local).getTime();
  return Number.isNaN(t) ? undefined : new Date(t + offsetMs).toISOString();
}
